import React, { useState } from "react";
import { Settings2 } from "lucide-react";
import { useTreasuryThresholds, useSetTreasuryThreshold } from "../hooks/useTreasury";

// Safety buffer = how far above "owed" a currency's held funds must be to
// count as healthy. Below owed is a shortfall (red); covered but inside the
// buffer is a warning (amber). Default applies to every currency unless
// overridden here.
function BufferSettings({ currencies }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const { data } = useTreasuryThresholds();
  const { mutate: save, isPending } = useSetTreasuryThreshold();

  const defaultPct = data?.defaultBufferPct ?? 2;
  const overrides = new Map((data?.overrides ?? []).map((o) => [o.currency, o.minBufferPct]));

  const commit = (currency) => {
    const raw = draft[currency];
    if (raw === undefined) return;
    const value = raw === "" ? null : Number(raw);
    if (value !== null && (Number.isNaN(value) || value < 0 || value > 100)) return;
    save({ currency, minBufferPct: value });
    setDraft((d) => {
      const next = { ...d };
      delete next[currency];
      return next;
    });
  };

  return (
    <div className="mb-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex gap-2 items-center text-sm font-medium text-[#013941] hover:underline"
      >
        <Settings2 className="w-4 h-4" />
        Safety buffer: {defaultPct}% by default {open ? "(hide)" : "(edit per currency)"}
      </button>
      {open && (
        <div className="p-4 mt-2 rounded border border-gray-200 bg-gray-50/50">
          <p className="mb-3 text-xs text-gray-500">
            Held must exceed owed by this percentage to count as healthy. Leave blank to use the {defaultPct}% default.
          </p>
          <div className="flex flex-wrap gap-4">
            {currencies.map((currency) => {
              const current = overrides.get(currency);
              const value = draft[currency] ?? (current ?? "");
              return (
                <label key={currency} className="flex gap-2 items-center text-sm text-gray-700">
                  <span className="w-12 font-medium">{currency}</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={value}
                    placeholder={String(defaultPct)}
                    onChange={(e) => setDraft((d) => ({ ...d, [currency]: e.target.value }))}
                    onBlur={() => commit(currency)}
                    onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                    disabled={isPending}
                    className="px-2 py-1 w-20 text-sm rounded border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="text-gray-400">%</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default BufferSettings;
